import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm, change } from 'redux-form';
import { createValidator, required } from '../../../utils/formValidations';
import purchaserValidations from '../../../utils/purchaserValidations';
import { getStoredPurchaser, setStoredPurchaser } from '../../../utils/storedPurchaser';
import getSafeCountryCallingCode from '../../../utils/phone';
import PurchaserForm from './PurchaserForm';
import { getDoc2BrandMap } from '../../../utils/documentMaps';
import { setError } from '../../../actions';
import { formErrors } from '../../../actions/purchase';
import { getUserByWalletType, walletTypeNeedsUserInPurchaser } from '../../../utils/loyalty';
import parsePurchaser from '../../../utils/purchase/parsePurchaser';
import { getEnabledBillingCompanyDocuments } from '../../../utils/purchase/billingCompanyDocuments';

const FORM = 'purchaser';
const validatePurchaser = createValidator(purchaserValidations);
const validateQuickPurchaser = createValidator({ ...purchaserValidations, legalTerms: required });
const validateQuickPurchaserWithoutLegal = createValidator({
  ...purchaserValidations,
  legalTerms: null,
});

/** Normalizes a billing-company document for identity comparisons. */
const normalizeBillingCompanyDocument = (value) => String(value || '').replace(/\D/g, '');

/** Normalizes a billing-company document type for identity comparisons. */
const normalizeBillingCompanyDocumentType = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

export const mapStateToProps = (state) => {
  const { purchase, whitelabelConfig } = state;
  const { env, features } = whitelabelConfig;
  const purchaseJs = purchase.toJS();
  const { walletType } = purchaseJs;

  const userNeededForPurchaser =
    walletTypeNeedsUserInPurchaser(walletType) && getUserByWalletType(walletType);

  const {
    documentId,
    documentType,
    purchaserFirstName,
    purchaserLastName,
    purchaserSecondLastName,
    email,
    phone,
    phoneCountry,
    isExchange,
    phoneCode,
    addressAttributes,
    billingCompanyId,
    taxpayerLegalName,
    taxpayerAddress,
    billingRecipientType,
  } = purchaseJs;

  /**
   * The initial values for the purchaser form are created, they are created with the purchase data if exits,
   * or with the local storage data.
   */
  const storedPurchaser = getStoredPurchaser();
  const billingCompanyFlowConfigured =
    getEnabledBillingCompanyDocuments(
      features.BILLING_COMPANY_ENABLED,
      features.BILLING_COMPANY_AVAILABLE_DOCUMENTS,
    ).length > 0;
  const doc2BrandMapPurchaser = getDoc2BrandMap('purchaser');
  const purchaserDocumentsOptions = Object.values(doc2BrandMapPurchaser);
  const initialDocumentId =
    documentId || (!isExchange ? storedPurchaser?.documentId : undefined) || '';
  const initialDocumentType =
    documentType ||
    (!isExchange ? storedPurchaser?.documentType : undefined) ||
    purchaserDocumentsOptions[0] ||
    'CC';
  // What makes a stored name unsafe to reuse is that it belongs to a company resolved for another
  // document. Backend company identity is the (document type, document number) pair — the same
  // digits under another type are another company — so both halves have to match. Storage written
  // before the type was persisted no longer hydrates: a fresh lookup beats a stale identity.
  const storedPurchaserHasCompany =
    billingCompanyFlowConfigured && Boolean(storedPurchaser?.billingCompanyId);
  const initialDocumentTypeValue = normalizeBillingCompanyDocumentType(initialDocumentType);
  const storedDocumentMatches =
    billingCompanyFlowConfigured &&
    !isExchange &&
    Boolean(normalizeBillingCompanyDocument(initialDocumentId)) &&
    normalizeBillingCompanyDocument(initialDocumentId) ===
      normalizeBillingCompanyDocument(storedPurchaser?.documentId) &&
    Boolean(initialDocumentTypeValue) &&
    initialDocumentTypeValue === normalizeBillingCompanyDocumentType(storedPurchaser?.documentType);
  const storedCompanyMatchesDocument = storedDocumentMatches && !billingCompanyId;
  const storedBillingCompanyName =
    storedPurchaser?.taxpayerLegalName || storedPurchaser?.purchaserFirstName;
  const storedBillingCompany =
    storedCompanyMatchesDocument && storedPurchaser?.billingCompanyId && storedBillingCompanyName
      ? {
          id: String(storedPurchaser.billingCompanyId),
          name: storedBillingCompanyName,
          address: storedPurchaser.taxpayerAddress || '',
          document: String(storedPurchaser.documentId),
          documentType: normalizeBillingCompanyDocumentType(storedPurchaser.documentType),
        }
      : null;
  const serverBillingCompany =
    billingCompanyFlowConfigured && billingCompanyId && (taxpayerLegalName || purchaserFirstName)
      ? {
          id: String(billingCompanyId),
          name: taxpayerLegalName || purchaserFirstName,
          address: taxpayerAddress || '',
          document: String(documentId || ''),
          // The purchase is the trusted source, so the company carries the type the form starts
          // with. Reading it back from `initialDocumentType` keeps the pair consistent with the
          // rendered `documentType` even when the purchase itself arrives without one.
          documentType: initialDocumentTypeValue,
        }
      : null;
  const initialBillingCompany = serverBillingCompany || storedBillingCompany;
  const initialBillingCompanyId =
    billingCompanyId || (storedCompanyMatchesDocument ? storedPurchaser?.billingCompanyId : null);
  const phoneCountryValue =
    phoneCountry || storedPurchaser?.phoneCountry || env.lang?.default?.substring(3) || 'MX';
  const phoneCodeValue = getSafeCountryCallingCode(phoneCountryValue, phoneCode || '');
  const initialValues = {
    documentId: initialDocumentId,
    documentType: initialDocumentType,
    purchaserFirstName:
      purchaserFirstName ||
      (!isExchange && (!storedPurchaserHasCompany || storedDocumentMatches)
        ? storedPurchaser?.purchaserFirstName
        : undefined) ||
      '',
    purchaserLastName:
      purchaserLastName || (!isExchange ? storedPurchaser?.purchaserLastName : undefined) || '',
    purchaserSecondLastName:
      purchaserSecondLastName ||
      (!isExchange ? storedPurchaser?.purchaserSecondLastName : undefined) ||
      '',
    email: email || (!isExchange ? storedPurchaser?.email : undefined) || '',
    phone: phone || (!isExchange ? storedPurchaser?.phone : undefined) || '',
    phoneCountry: phoneCountryValue,
    phoneCode: phoneCodeValue,
    promotionalTerms: features?.PROMOTIONAL_TERMS_BY_DEFAULT,
    cdsFrequentPassengerOptIn: purchaseJs.meta?.frequent_passenger_enrollment === 'true',
    addressAttributes: addressAttributes || storedPurchaser?.addressAttributes || {},
    // MID-555: hydrate the invoice recipient from the stored purchase so the
    // BillingRecipientSection switches reflect what the backend already holds
    // after a reload (F5). Omitted when absent (every non-Fospina tenant), so no
    // other brand starts carrying the field. Only the purchase is trusted here —
    // the recipient is not persisted to local storage.
    ...(billingRecipientType && { billingRecipientType }),
    ...(billingCompanyFlowConfigured && {
      taxpayerLegalName:
        taxpayerLegalName || (storedBillingCompany ? storedBillingCompany.name : ''),
      taxpayerAddress:
        taxpayerAddress || (storedBillingCompany ? storedBillingCompany.address : ''),
      billingCompanyId: initialBillingCompanyId || '',
    }),
    ...(userNeededForPurchaser && { ...parsePurchaser(userNeededForPurchaser) }),
  };

  return {
    initialValues,
    initialBillingCompany,
  };
};

/**
 * Maps purchaser form submission errors to the global error UI.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {object} ownProps - Connected component props.
 * @returns {object} Purchaser form dispatch callbacks.
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  onSubmitFail: (fields) => {
    if (ownProps.onSubmitPurchaserFail) {
      ownProps.onSubmitPurchaserFail();
    }
    // eslint-disable-next-line prettier/prettier
    dispatch(setError(200, 'check_the_fields_in_red', 'warning', false, null, null, null, 'fixed'));
    dispatch(formErrors(1, fields));
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: FORM,
    destroyOnUnmount: false,
    validate: (values, { quickCheckoutAgreementPolicy }) => {
      if (!quickCheckoutAgreementPolicy) return validatePurchaser(values);
      return quickCheckoutAgreementPolicy.legalRequired
        ? validateQuickPurchaser(values)
        : validateQuickPurchaserWithoutLegal(values);
    },
    onSubmitSuccess: (result, dispatch, props) => {
      setStoredPurchaser(props.values);
    },
    onChange: (values, dispatch) => {
      const { documentId, documentType } = values;
      if ((documentType === 'CPF' || documentType === 'CNPJ') && documentId) {
        const valueWithOnlyDigits = documentId.replace(/\D/g, '');
        dispatch(change('purchaser', 'documentId', valueWithOnlyDigits));
      }
    },
  }),
)(PurchaserForm);
